import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { signInWithGoogle, handleRedirectResult } from "../firebase";
import { Mail, Lock, Eye, EyeOff, Brain, AlertCircle, Loader } from "lucide-react";

const BETA_CODE = 'NOBISHIRO2026';

const getAuthErrorMessage = (error) => {
  const code = error?.code || '';
  const messages = {
    'auth/email-already-in-use':    'このメールアドレスはすでに登録されています。ログインしてください。',
    'auth/invalid-email':           'メールアドレスの形式が正しくありません。',
    'auth/weak-password':           'パスワードは6文字以上で入力してください。',
    'auth/user-not-found':          'メールアドレスまたはパスワードが間違っています。',
    'auth/wrong-password':          'メールアドレスまたはパスワードが間違っています。',
    'auth/invalid-credential':      'メールアドレスまたはパスワードが間違っています。',
    'auth/too-many-requests':       'ログイン試行回数が多すぎます。しばらく時間をおいてから再度お試しください。',
    'auth/network-request-failed':  'ネットワークエラーが発生しました。接続を確認してください。',
    'auth/popup-closed-by-user':    'ログインがキャンセルされました。',
    'auth/cancelled-popup-request': 'ログインがキャンセルされました。',
    'auth/popup-blocked':           'ポップアップがブロックされました。ブラウザの設定をご確認ください。',
    'auth/user-disabled':           'このアカウントは無効化されています。管理者にお問い合わせください。',
    'auth/requires-recent-login':   '再ログインが必要です。一度ログアウトして再度ログインしてください。',
  };
  return messages[code] || error?.message || '認証に失敗しました。もう一度お試しください。';
};
const BETA_KEY  = 'nobishiro_beta';

const Login = () => {
  const { user, signIn, signUp, loading } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: "", password: "", confirmPassword: "", displayName: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirectProcessing, setIsRedirectProcessing] = useState(false);
  const [betaActivated, setBetaActivated] = useState(false);

  // ベータ招待URL処理: ?beta=NOBISHIRO2026
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const betaParam = params.get('beta');
    if (betaParam === BETA_CODE) {
      localStorage.setItem(BETA_KEY, 'true');
      localStorage.removeItem('nobishiro_beta_banner_dismissed');
      setBetaActivated(true);
    }
    // リファラルコードを保存（ref=がある場合、beta=も自動付与）
    const refParam = params.get('ref');
    if (refParam) {
      localStorage.setItem('nobishiro_referral', refParam);
      // 紹介URL経由は自動的にベータ扱いにする
      localStorage.setItem('nobishiro_beta', 'true');
      setBetaActivated(true);
    }
    if (betaParam || refParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // redirect結果処理（popup失敗時のフォールバック用）
  useEffect(() => {
    const processRedirect = async () => {
      setIsRedirectProcessing(true);
      try {
        const result = await handleRedirectResult();
        if (result.success && result.user) {
          return; // onAuthStateChangedが自動的にdashboardへ遷移
        }
        if (!result.noResult && result.error) {
          setError("Googleログインに失敗しました: " + result.error);
        }
      } catch (err) {
        // エラーは無視（redirectしていない場合は正常）
      } finally {
        setIsRedirectProcessing(false);
      }
    };
    processRedirect();
  }, []);

  if (user && !loading) return <Navigate to="/dashboard" replace />;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) { setError("メールアドレスとパスワードを入力してください"); return false; }
    if (!isLogin) {
      if (!formData.displayName) { setError("表示名を入力してください"); return false; }
      if (formData.password !== formData.confirmPassword) { setError("パスワードが一致しません"); return false; }
      if (formData.password.length < 6) { setError("パスワードは6文字以上で入力してください"); return false; }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true); setError("");
    try {
      if (isLogin) { await signIn(formData.email, formData.password); }
      else { await signUp(formData.email, formData.password, formData.displayName); }
    } catch (error) {
      setError(getAuthErrorMessage(error));
    } finally { setIsSubmitting(false); }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true); setError("");
    try {
      const result = await signInWithGoogle();
      if (result && !result.success && !result.redirecting) {
        setError("Googleログインに失敗しました: " + (result.error || "不明なエラー"));
      }
    } catch (error) {
      setError("Googleログインに失敗しました");
    } finally { setIsSubmitting(false); }
  };



  const isWebView = /Line|FBAN|FBAV|Instagram|Twitter|MicroMessenger|WebView|wv/.test(navigator.userAgent)
    || (/iPhone|iPod|iPad/.test(navigator.userAgent) && !/Safari/.test(navigator.userAgent));

  if (loading || isRedirectProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{isRedirectProcessing ? "Googleアカウントで認証中..." : "認証状態を確認中..."}</p>
          {isRedirectProcessing && <p className="text-sm text-gray-400 mt-2">そのままお待ちください</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-2xl font-bold text-indigo-600 mb-2">
            <Brain className="w-8 h-8" />NOBISSHIRO
          </div>
          <p className="text-gray-600">{isLogin ? "アカウントにサインインしてください" : "新しいアカウントを作成"}</p>
        </div>

        {betaActivated && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🎉</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>ベータ版招待を受け付けました！</div>
              <div style={{ fontSize: 12, color: '#15803d' }}>ログインしてダッシュボードをお試しください</div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" /><span className="text-sm">{error}</span>
          </div>
        )}

        {isWebView && (
          <div style={{ background: "#fffbeb", border: "1.5px solid #f59e0b", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>Safariで開いてください</div>
                <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, marginBottom: 10 }}>
                  LINEやInstagram等のアプリ内ブラウザではGoogleログインができません。SafariまたはChromeで開いてください。
                </div>
                <button onClick={() => {
                    navigator.clipboard?.writeText(window.location.href)
                      .then(() => alert("URLをコピーしました！\nSafariのアドレスバーに貼り付けてアクセスしてください。"))
                      .catch(() => alert("SafariでこのURLを開いてください：\n" + window.location.href));
                  }}
                  style={{ width: "100%", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  📋 URLをコピーしてSafariで開く
                </button>
              </div>
            </div>
          </div>
        )}



        <button onClick={handleGoogleLogin} disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors disabled:opacity-50 mb-6">
          {isSubmitting ? <Loader className="w-5 h-5 animate-spin text-gray-500" /> : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Googleでログイン
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
          <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">または</span></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">表示名</label>
              <div className="relative">
                <input type="text" name="displayName" value={formData.displayName} onChange={handleInputChange}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="山田 太郎" disabled={isSubmitting} />
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス</label>
            <div className="relative">
              <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="example@company.com" disabled={isSubmitting} />
              <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">パスワード</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleInputChange}
                className="w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={isLogin ? "パスワード" : "6文字以上"} disabled={isSubmitting} />
              <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600" disabled={isSubmitting}>
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">パスワード確認</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="パスワードを再入力" disabled={isSubmitting} />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
          )}
          <button type="submit" disabled={isSubmitting}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {isSubmitting ? (<><Loader className="w-4 h-4 animate-spin" />処理中...</>) : (isLogin ? "ログイン" : "アカウント作成")}
          </button>
        </form>

        {isLogin && <div className="text-center mt-4"><button className="text-sm text-indigo-600 hover:text-indigo-700">パスワードを忘れた方</button></div>}

        <div className="text-center mt-6">
          <span className="text-sm text-gray-600">{isLogin ? "アカウントをお持ちでない方は " : "既にアカウントをお持ちの方は "}</span>
          <button onClick={() => { setIsLogin(!isLogin); setError(""); setFormData({ email: "", password: "", confirmPassword: "", displayName: "" }); }}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium" disabled={isSubmitting}>
            {isLogin ? "アカウント作成" : "ログイン"}
          </button>
        </div>

        <div className="text-center mt-6 text-xs text-gray-400">
          Googleでログインすることで、
          <a href="/terms" className="text-indigo-600 hover:underline mx-1">利用規約</a>および
          <a href="/privacy" className="text-indigo-600 hover:underline mx-1">プライバシーポリシー</a>に同意したものとみなします。
        </div>
        <div className="text-center mt-3 text-xs text-gray-400">© 2026 富永コーチングカントリークラブ株式会社</div>
      </div>
    </div>
  );
};

export default Login;
